// Аналитика Mini App — Google Analytics 4, тот же проект Firebase, что у iOS и Android (поток «web»). Единственное место,
// что трогает gtag и dataLayer. Включается только идентификатором потока из окружения (VITE_GA_MEASUREMENT_ID); без него,
// в dev и в mock-режиме — тихо ничего не делает. Режим проверки (npm run dev:check, ARCHITECTURE §11) — dev-сервер с моком,
// аналитика включена, каждое событие с debug_mode (видно в DebugView); в боевую сборку флаг не попадает (vite.config). Номер Telegram и имя человека в аналитику НЕ уходят: клиент опознаётся
// нашим идентификатором установки (тот же X-Device-Id), куки не используются (в окне Telegram Web они сторонние).
// События: screen_view (имена словаря v1, events.screenName), tg_launch, content_open (useContentOpen), действия a_* (events.ts), battle_play / level_up,
// stars_invoice, действия словаря M2 (избранное, поиск, шаринг, прогноз, язык, ролики, карточки спонсоров). Полный список
// имён — catalog.ts. Деньги и экономика точно считаются на сервере — здесь поведение.
import { DEVICE_ID, setActionListener } from '../api/client';
import { lang } from '../i18n/i18n';
import { splitStartParam } from '../deeplink';
import { isMock, isTelegram, startParam, telegramPlatform } from '../telegram/webapp';
import { actionEventName, actionName, actionResult, campaignOf, campaignQuery, isBattleReport, levelMarks, milestoneOf, screenContentId, screenName } from './events';
import { Profile } from './profile';
import { noteScreen, savedAnalyticsUser, setPropsSink, setSink, setUserSink, track, UNLOAD_LIMIT_MS, type Params } from './track';

declare global { interface Window { dataLayer?: unknown[] } }

/** Режим проверки: true только на dev-сервере в режиме check (define в vite.config); в сборке — литерал false. */
declare const __ANALYTICS_DEBUG__: boolean;

const ID: string = import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';
const enabled = Boolean(ID) && (__ANALYTICS_DEBUG__ || (!import.meta.env.DEV && !isMock));
/** Номер релиза сборки (scripts/deploy.sh: VITE_RELEASE, как в журнале релизов docs/STATE.md); вне выката — dev. */
const APP_VERSION: string = import.meta.env.VITE_RELEASE || 'dev';

function gtag(..._args: unknown[]): void {
  // gtag.js читает именно объект arguments, не массив
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer!.push(arguments);
}

let profile: Profile | null = null;
let utm = '';
let lastScreen = '';
let battleLevel = 0;

/** Контекст каждого события: где человек, насколько он «старый» и платил ли — чтобы любое событие резалось по этим осям;
 *  спорт и релиз — обязательные поля веба по словарю v1 (§2). */
const context = (): Params => ({
  screen: lastScreen || undefined, lvl: battleLevel || undefined,
  age_d: profile?.ageDays(Date.now()), sess_n: profile?.sessions, payer: profile?.payer ? 1 : 0,
  sport: 'cricket', app_version: APP_VERSION,
  ...(__ANALYTICS_DEBUG__ ? { debug_mode: true } : {}),
});

/** Веха — один раз за жизнь на устройстве: m_<имя> с возрастом в минутах (через сколько после первого запуска). */
function milestone(name: string): void {
  if (profile?.reach(name)) track(`m_${name}`.slice(0, 40) as `m_${string}`, { since_min: profile.ageMinutes(Date.now()) });
}

/** Откуда пришли на запись истории (ключ location маршрутизатора): экран, на котором нажали, — один раз, при первом
 *  появлении записи. Возврат «назад» к той же записи сохраняет её источник. */
const sources = new Map<string, string>();
let lastContent: string | undefined;
export interface ScreenLocation { pathname: string; search: string; key: string }

/** Экран показан: screen_view (один раз на показ — смена имени или номера сущности) и источник записи истории.
 *  from — источник новой записи явно (холодный старт по ссылке — link), иначе — экран, на котором был человек. */
export function trackScreen(loc: ScreenLocation, from?: string): void {
  if (!sources.has(loc.key)) sources.set(loc.key, from ?? (lastScreen || 'unattributed'));
  const name = screenName(loc.pathname, loc.search);
  // номер матча / игрока / турнира — параметром, не в имени экрана
  const id = screenContentId(loc.pathname);
  if (name === lastScreen && id === lastContent) return;
  const prev = lastScreen;
  lastScreen = name;
  lastContent = id;
  noteScreen(name);
  track('screen_view', { screen_name: name, prev_screen: prev || undefined, content_id: id });
}

/** source для content_open: экран, на котором нажали (словарь v1, §4.2); неизвестно — unattributed. */
export const sourceOf = (key: string): string => sources.get(key) ?? 'unattributed';

// бой: «попробовал поиграть» — первый отчёт сессии; новый уровень — по ответу отчёта; время и тапы — при уходе
let played = false;
const battle = { active: 0, last: 0, taps: 0, kills: 0 };
function battleReport(body: unknown, sent: unknown): void {
  const now = Date.now();
  if (!played) { played = true; track('battle_play'); milestone('first_battle'); }
  // отчёты идут раз в ~5 с; пауза дольше минуты — человек отходил, это время боем не считаем
  if (battle.last && now - battle.last <= 60_000) battle.active += now - battle.last;
  battle.last = now;
  const report = sent as { taps?: number; kills?: number } | null;
  battle.taps += Number(report?.taps) || 0;
  battle.kills += Number(report?.kills) || 0;
  const level = Number((body as { battle?: { level?: number } } | null)?.battle?.level) || 0;
  if (battleLevel && level > battleLevel) {
    track('level_up', { level, character: 'battle' });
    for (const m of levelMarks(battleLevel, level)) milestone(`level_${m}`);
  }
  if (level) battleLevel = level;
}

/** Ждать, пока событие уйдёт в сеть (HQ-607). event_callback gtag не годится: он зовётся, когда событие встало в пачку,
 *  а пачка уходит через секунды. Признак отправки — запрос g/collect (sendBeacon или fetch keepalive), начатый после
 *  события: его видит PerformanceObserver ресурсов, пачка уходит целиком. Браузер без наблюдения за ресурсами — остаётся
 *  event_callback (лучше, чем ничего). Возвращает добавки к параметрам события. */
function awaitDispatch(done: () => void): Params | { event_callback: () => void; event_timeout: number } {
  const Observer = window.PerformanceObserver;
  if (!Observer?.supportedEntryTypes?.includes('resource')) return { event_callback: done, event_timeout: UNLOAD_LIMIT_MS };
  const since = performance.now();
  const observer = new Observer((list) => {
    if (!list.getEntries().some((e) => e.startTime >= since && /\/g\/collect(?:[?#]|$)/.test(e.name))) return;
    observer.disconnect();
    done();
  });
  try { observer.observe({ type: 'resource' }); } catch { return { event_callback: done, event_timeout: UNLOAD_LIMIT_MS }; }
  setTimeout(() => observer.disconnect(), UNLOAD_LIMIT_MS);
  return {};
}

/** Ушёл (свернул, закрыл, переключился): с какого экрана и сколько был; итог боя за это время. */
let shownAt = Date.now();
function onHide(): void {
  track('app_hide', { sec: Math.round((Date.now() - shownAt) / 1000) });
  if (battle.taps + battle.kills > 0) {
    track('battle_time', { sec: Math.max(1, Math.round(battle.active / 1000)), taps: battle.taps, kills: battle.kills });
    battle.active = 0; battle.last = 0; battle.taps = 0; battle.kills = 0;
  }
}

/** Наш внутренний номер пользователя (НЕ номер Telegram) — когда бэкенд начнёт его отдавать: склейка с данными сервера. */
export function setAnalyticsUser(id: string | number | null | undefined): void {
  if (enabled && id != null && id !== '') gtag('config', ID, { user_id: String(id), send_page_view: false });
}

export function initAnalytics(): void {
  if (!enabled) { setSink(null); return; }
  let storage: Storage | null = null;
  try { storage = window.localStorage; } catch { /* приватный режим */ }
  const now = Date.now();
  profile = new Profile(storage, now);
  const { dest, label } = splitStartParam(startParam());
  const session = campaignOf(label, dest);
  const first = profile.touch(session, now);
  utm = campaignQuery(session);

  window.dataLayer = window.dataLayer ?? [];
  gtag('js', new Date());
  gtag('config', ID, {
    // все пачки — sendBeacon: переживает выгрузку страницы (перезагрузка после смены языка, закрытие окна)
    transport_type: 'beacon',
    send_page_view: false, client_id: DEVICE_ID, client_storage: 'none', allow_google_signals: false, allow_ad_personalization_signals: false,
    // кампания сессии: Google читает utm из адреса «страницы»; явные поля — на случай, если адрес не разберёт
    page_location: `${location.origin}/?${utm}`,
    campaign_source: session.source, campaign_medium: session.medium, campaign_name: session.name, campaign_content: session.content || undefined,
    user_properties: {
      client: 'telegram', tg_platform: telegramPlatform(), app_language: lang, in_telegram: isTelegram,
      // первое касание — навсегда: по нему считается окупаемость рекламы
      first_source: first.source, first_medium: first.medium, first_campaign: first.name, first_content: first.content || undefined,
    },
    ...(__ANALYTICS_DEBUG__ ? { debug_mode: true } : {}),
  });
  setUserSink(setAnalyticsUser);
  setPropsSink((props) => gtag('set', 'user_properties', props));
  setAnalyticsUser(savedAnalyticsUser());
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ID)}`;
  document.head.appendChild(s);
  // tg_launch — первым событием сессии, затем накопленное до запуска (первый экран холодного старта)
  setSink((name, params, done) => {
    if (name === 'stars_invoice' && params.status === 'paid') { profile?.markPayer(); milestone('first_stars_paid'); }
    if (name === 'stars_invoice') milestone('first_stars_invoice');
    // экран — «страницей» с метками кампании: Google раскладывает просмотры по кампаниям сессии
    const page: Params = name === 'screen_view' && typeof params.screen_name === 'string'
      ? { page_title: params.screen_name, page_location: `${location.origin}/${params.screen_name}?${utm}` } : {};
    // done — вызывающий ждёт фактической отправки (перезагрузка после смены языка), предел — у вызывающего
    const wait = done ? awaitDispatch(done) : {};
    gtag('event', name, { ...context(), ...page, ...params, ...wait });
    // вехи действий словаря — после самого события
    if (name === 'favorite_add') milestone('first_favorite');
    if (name === 'prediction_submit' && params.result === 'ok') milestone('first_prediction');
  }, [['tg_launch', { source: session.source, medium: session.medium, campaign: session.name, content: session.content || undefined,
    dest: dest ? actionName('POST', dest)?.replace(/^a_/, '') : 'home', tg_platform: telegramPlatform(), is_new: profile.isNew ? 1 : 0 }]]);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') onHide(); else shownAt = Date.now(); });
  setActionListener((a) => {
    const ok = a.status >= 200 && a.status < 300;
    if (isBattleReport(a.path)) { if (ok) battleReport(a.body, a.sent); return; }
    const name = actionEventName(a.method, a.path);
    if (!name) return;
    track(name, { result: actionResult(a.status, a.code, a.reason) });
    if (!ok) return;
    const m = milestoneOf(name);
    if (m) milestone(m);
    // выбор команды (она же клуб-гильдия) и страны — один адрес world/player/, различает тело; выбора роли нет (В1, HQ-782)
    const sent = a.sent as { team_id?: unknown; country_id?: unknown } | null;
    if (/^a_world_player$/.test(name) && sent) {
      if (sent.team_id != null) milestone('team_pick');
      if (sent.country_id != null) milestone('country_pick');
    }
  });
}
